import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { richTextValidation } from 'validators';
import { Box, Button, Row, TextField } from 'seagull';
import { RichTextEditor } from 'seagull/components/RichTextEditor/RichTextEditor';
import { getTestIds } from 'seagull/utils/TestIds';
import { DisplayScopeCheckbox } from '~/components/DisplayScopeCheckbox/DisplayScopeCheckbox';
import { useStore } from '~/contexts';
import { useCreateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useCreateCustomPageTemplate';
import { useTemplateSaveErrorHandler } from '~/hooks/reactQuery/mutations/useTemplateSaveErrorHandler';
import { useUpdateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useUpdateCustomPageTemplate';
import { useCustomPageTemplates } from '~/hooks/reactQuery/queries/useCustomPageTemplates';
import { useLocaleKeys } from '~/i18n';
import { pageTitleValidation } from '~/models/custom-page-template/pageTitleValidation';
import { templateNameValidation } from '~/models/custom-page-template/templateNameValidation';
import { selectors } from '~/store/state-selectors';
import { connectControllerToRichTextEditor, connectControllerToTextField } from '~/utils/react-hook-form.utils';
import s from './TextTemplateFormModal.module.scss';
export const GetTextTemplateFormModalTID = (parentTestId)=>getTestIds(parentTestId);
export const TextTemplateForm = (param)=>{
    let { mode, template, onBack, onSaved } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetTextTemplateFormModalTID(), []);
    const businessId = useStore(selectors.user.businessId);
    const { templates: existingTemplates } = useCustomPageTemplates();
    const { control, handleSubmit, setError, setValue, watch } = useForm({
        mode: 'onChange',
        defaultValues: {
            name: mode === 'edit' && template ? template.name : '',
            pageTitle: mode === 'edit' && template ? template.pageTitle : '',
            displayScope: mode === 'edit' && template ? template.displayScope : 'proposalOnly',
            content: mode === 'edit' && template ? template.text.content : ''
        }
    });
    const { createTemplate, isLoading: isCreateLoading } = useCreateCustomPageTemplate({
        onSuccess: ()=>onSaved()
    });
    const { updateTemplate, isLoading: isUpdateLoading } = useUpdateCustomPageTemplate({
        onSuccess: ()=>onSaved()
    });
    const handleSaveError = useTemplateSaveErrorHandler({
        mode,
        setNameError: (message)=>setError('name', {
                message
            })
    });
    const isLoading = isCreateLoading || isUpdateLoading;
    const displayScope = watch('displayScope');
    const onSubmit = handleSubmit((values)=>{
        if (isLoading) return;
        const payload = {
            type: 'text',
            name: values.name.trim(),
            pageTitle: values.pageTitle.trim(),
            displayScope: values.displayScope,
            text: {
                content: values.content
            }
        };
        if (mode === 'create') {
            createTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                data: payload
            }, {
                onError: handleSaveError
            });
        } else if (template) {
            updateTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                templateId: template.id,
                data: payload
            }, {
                onError: handleSaveError
            });
        }
    });
    const toggleDisplayScope = ()=>{
        setValue('displayScope', displayScope === 'proposalOnly' ? 'proposalAndAgreement' : 'proposalOnly');
    };
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.TextTemplateFormModalTestIds.Root(),
        className: s.formContent,
        children: [
            /*#__PURE__*/ _jsx(Controller, {
                name: "name",
                control: control,
                rules: templateNameValidation({
                    t: t.customPages.templates,
                    existingTemplates,
                    excludeTemplateId: template === null || template === void 0 ? void 0 : template.id
                }),
                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({}, connectControllerToTextField(renderProps, {
                        alwaysShowErrors: true
                    })), {
                        testId: testIds.TextTemplateFormModalTestIds.NameField(),
                        label: t.customPages.templates.nameLabel(),
                        bi: {
                            name: 'text_template_form_name'
                        }
                    }))
            }),
            /*#__PURE__*/ _jsx(Controller, {
                name: "pageTitle",
                control: control,
                rules: pageTitleValidation(t.customPages),
                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({}, connectControllerToTextField(renderProps, {
                        alwaysShowErrors: true
                    })), {
                        testId: testIds.TextTemplateFormModalTestIds.PageTitleField(),
                        label: t.customPages.templates.pageTitleLabel(),
                        bi: {
                            name: 'text_template_form_page_title'
                        }
                    }))
            }),
            /*#__PURE__*/ _jsx(Controller, {
                name: "content",
                control: control,
                rules: richTextValidation(undefined, {
                    required: t.customPages.templates.fieldRequired()
                }),
                render: (renderProps)=>/*#__PURE__*/ _jsx(RichTextEditor, _object_spread_props(_object_spread({}, connectControllerToRichTextEditor(renderProps)), {
                        testId: testIds.TextTemplateFormModalTestIds.RichTextEditor(),
                        editorHeight: 280,
                        bi: {
                            name: 'text_template_form_editor'
                        }
                    }))
            }),
            /*#__PURE__*/ _jsx(DisplayScopeCheckbox, {
                testId: testIds.TextTemplateFormModalTestIds.DisplayScopeCheckbox(),
                checked: displayScope === 'proposalAndAgreement',
                onChange: toggleDisplayScope
            }),
            /*#__PURE__*/ _jsxs(Row, {
                className: s.formFooter,
                justifyContent: "center",
                gap: 12,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        onClick: onBack,
                        testId: testIds.TextTemplateFormModalTestIds.CancelButton(),
                        bi: {
                            name: 'text_template_form_cancel'
                        },
                        children: t.customPages.templates.cancel()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primary",
                        loading: isLoading,
                        onClick: onSubmit,
                        testId: testIds.TextTemplateFormModalTestIds.SaveButton(),
                        bi: {
                            name: 'text_template_form_save'
                        },
                        children: t.customPages.templates.save()
                    })
                ]
            })
        ]
    });
};
